import { useInfiniteQuery } from "@tanstack/react-query";
import { AxiosError } from "axios";

import type { EntityHistoryEntry } from "@/components/ui/v2/history";
import { instance } from "@/lib/axios/instance";
import { PagedResponse } from "@/types/response/PagedResponse";

interface UseEntityHistoryParams {
  queryKey: readonly unknown[];
  apiPath: string;
  from: Date;
  to?: Date;
  pageSize?: number;
  enabled?: boolean;
}

export function useEntityHistory({
  queryKey,
  apiPath,
  from,
  to,
  pageSize = 30,
  enabled = true,
}: UseEntityHistoryParams) {
  return useInfiniteQuery<PagedResponse<EntityHistoryEntry>, AxiosError>({
    queryKey,
    initialPageParam: 1,
    queryFn: ({ pageParam }) =>
      instance
        .get<PagedResponse<EntityHistoryEntry>>(apiPath, {
          params: {
            from: from.toISOString(),
            ...(to && { to: to.toISOString() }),
            page: pageParam,
            pageSize,
          },
        })
        .then((res) => res.data),
    getNextPageParam: (lastPage, allPages) => {
      const totalFetched = allPages.reduce(
        (acc, page) => acc + page.data.length,
        0
      );
      return totalFetched < lastPage.totalRecords
        ? allPages.length + 1
        : undefined;
    },
    refetchOnReconnect: false,
    staleTime: 20 * 60 * 1000,
    enabled,
  });
}
